// QuestionSetInput.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Row,
  Col,
  Card,
  CardBody,
  Container,
  Button,
  Badge,
  UncontrolledDropdown, 
  DropdownToggle, 
  DropdownMenu, 
  DropdownItem
} from "reactstrap";
import { withTranslation } from "react-i18next";

import axiosInstance from "utils/axiosInstance";
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { getLastPage } from 'utils/history';
import { useDispatch } from "react-redux";

import { uploadImage, fetchImage } from "utils/images/imageManager";
import { fetchThumbnailsWithCache } from "utils/thumbnails/fetchThumbnailsWithCache";

import home4 from 'assets/images/info/home4.png';
import placeholder_image from "assets/images/info/card-placeholder-medium.png";
import { ArrowLeft, MessageSquareText, CircleChevronRight, CheckCheck, RefreshCcw,
  TicketCheck, ChevronRight } from 'lucide-react';
import { MoreVertical, Edit2, Trash2 } from 'react-feather';

import { useAutoFontResize } from './useAutoFontResize';

import './QuestionSetInput.css';
import QuestionPane from './QuestionPane';
import CheckReveal from './CheckAnimation';
import BorderBox from './BorderAnimation';
import PreviewImageModal from './PreviewImageModal';
import QuestionSetProgress from './components/ProgressBar';
import BreadcrumbAboveImage from "components/Common/BreadcrumbAboveImage";
/*
function adjustFontSizeByTextLength(el, max = 20, min = 12) {
  if (!el) return;

  const length = el.textContent.length;
  const windowWidth = window.innerWidth;

  let size = max;

  console.log('length:', length);
  console.log('size:', size);

  // Shrink for longer text
  if (length > 5) size -= 0;
  if (length > 10) size -= 2;
  if (length > 25) size -= 6;
  if (length > 45) size = min;

  console.log('size:', size);

  // Further shrink on small screens
  if (windowWidth < 768) size -= 0;           // mobile
  if (windowWidth < 480 && length > 10) size -= 2;           // very small phones

  console.log('size:', size);

  // Clamp between min and max
  size = Math.max(min, Math.min(size, max));
  el.style.fontSize = `${size}px`;
}
*/

function transformToRenderedColumns(columnData, questionSet) {
  const result = [];
  let currentSection = {
    label: "",              // Fallback/default label
    description: "",
    sectionContent: [],
  };
  result.push(currentSection);

  for (const item of columnData) {
    if (item.type === "section") {
      // Start a new named section
      currentSection = {
        label: item.label,
        description: item.description || "",
        sectionContent: [],
      };
      result.push(currentSection);
    } else {
      // Add content to the current section (including the default one)
      const contentItem = { type: item.type, _id: item._id };

      if (item.type === "text") {
        contentItem.text = item.text;
      } else if (item.type === "image") {
        contentItem.imageUrl = item.image;
      } else if (item.type === "question") {
        contentItem.label = item.label;
      }

      currentSection.sectionContent.push(contentItem);
    }
  }

  // Remove default section if it ends up empty and wasn't followed by any content
  if (result.length === 1 && result[0].sectionContent.length === 0) {
    return [];
  }

  if (result[0].label === "" && result[0].sectionContent.length === 0) {
    return result.slice(1, result.length);
  }

  return result;
}


const QuestionSetInput = ({ t }) => {

  const { questionSetId } = useParams();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch({ type: 'SHOW_HEADER', payload: false }); // hide header
    dispatch({ type: 'SHOW_FOOTER', payload: false }); // hide footer

    var body = document.body;
    body.classList.remove("sidebar-enable");
    body.classList.remove("vertical-collpsed");

    return () => {
      dispatch({ type: 'SHOW_HEADER', payload: true }); // restore header
      dispatch({ type: 'SHOW_FOOTER', payload: true }); // restore footer
    };
  }, []);

  const checkRef = useRef();
  const answerTextareaRefs = useRef({});

  const handlePageBack = () => { 
    const topicPageUrl = myInfoQuestionSet.type==='InfoQuestionSet' 
      ? `/myInfo/topics/${myInfoQuestionSet?.topic?._id}`
      : `/myInfo/storeMenu/topics/${myInfoQuestionSet?.topic?._id}`

    const last = getLastPage();
    console.log('previous page:', last);
    if (["/myInfo/storeMenu", "/myInfo/infoFeed"].includes(last)) {
      navigate(last, { state: { __restoreScroll: true } });
    } else {
      navigate(topicPageUrl); 
    }
  }

  //--------------------------------------------------
  // Column States
  //--------------------------------------------------

  const [myInfoQuestionSet, setMyInfoQuestionSet] = useState([]);
  const [myInfoQuestionSetColumns, setMyInfoQuestionSetColumns] = useState([]);
  const [myInfoQuestionSetRenderColumns, setMyInfoQuestionSetRenderColumns] = useState([]);
  const [questionSetThumbnail, setQuestionSetThumbnail] = useState({});
  const [columnImages, setColumnImages] = useState({});
  const [questionSetRewardStatus, setQuestionSetRewardStatus] = useState({});

  const [questionSetLoading, setQuestionSetLoading] = useState(true);
  const [answerLoading, setAnswerLoading] = useState(true);
  const [dataFetchError, setDataFetchError] = useState(null);
  
  const [activeSectionIndex, setActiveSectionIndex] = useState(0);  


  //--------------------------------------------------
  // Answer States
  //--------------------------------------------------
  const [showActivationConditions, setShowActivationConditions] = useState(false);
  const [showVerificationRules, setShowVerificationRules] = useState(false);
  const [showAffectedAttributes, setShowAffectedAttributes] = useState(false);

  const [answers, setAnswers] = useState({}); // User inputs
  const [hasVerifiedAnswers, setHasVerifiedAnswers] = useState({}); // Track verified answers
  const [hasWrongAnswers, setHasWrongAnswers] = useState({});
  const [verifiedAnswers, setVerifiedAnswers] = useState({}); // Store verified inputs
  const [hasConfirmedAnswers, setHasConfirmedAnswers] = useState({}); // Track verified answers
  const [confirmedAnswers, setConfirmedAnswers] = useState({}); // Store confirmed inputs

  const [answerAwaitVerifying, setAnswerAwaitVerifying] = useState("");
  const [answerAwaitSaving, setAnswerAwaitSaving] = useState("");

  const [ruleVerificationStatus, setRuleVerificationStatus] = useState({}); 
  const [resolvedAnswerBuckets, setResolvedAnswerBuckets] = useState({});

  //------------------------------------------------
  // Input Change
  //------------------------------------------------

  // Handle input change
  const handleInputChange = (questionId, event) => {
    const newAnswer = event.target.value;

    setAnswers((prev) => ({ ...prev, [questionId]: newAnswer }));

    
    // Reset verification status when input changes
    setHasWrongAnswers((prev) => ({ ...prev, [questionId]: false }));
    setHasVerifiedAnswers((prev) => ({ ...prev, [questionId]: false }));
    // Reset confirmed answers when input changes
    setHasConfirmedAnswers((prev) => ({ ...prev, [questionId]: false }));
  };


  //--------------------------------------------------
  // Stats
  //--------------------------------------------------
  // Stats section Open States
  const [visibleStats, setVisibleStats] = useState({});        // tracks if it should *render*
  const [animatingStats, setAnimatingStats] = useState({});    // tracks if it's *visible or hiding*

  const toggleStatsOpen = (id) => {

    // Prevent toggling if animation is in progress (either showing or hiding)
    if (id in animatingStats && animatingStats[id] === false) return;

    setVisibleStats((prev) => {
      const nowVisible = !prev[id];

      // Show: immediately render + start visible animation
      if (nowVisible) {
        setAnimatingStats((a) => ({ ...a, [id]: true }));
        return { ...prev, [id]: true };
      }

      // Hide: start exit animation first, then unmount after delay
      setAnimatingStats((a) => ({ ...a, [id]: false }));
      setTimeout(() => {
        setVisibleStats((v) => ({ ...v, [id]: false }));
        setAnimatingStats((a) => {
          const updated = { ...a };
          delete updated[id];
          return updated;
        });
      }, 1200); // match exit animation duration

      return prev;
    });
  };


  //--------------------------------------------------
  // Load questionSet and columns on mount
  //--------------------------------------------------

  const fetchData = async () => {
    try {
      setQuestionSetLoading(true);
      setDataFetchError(null);

      // Run both requests in parallel
      const [questionSetResponse, columnResponse, rewardStatusResponse] = await Promise.all([
        axiosInstance.get(`/knovia/myInfo/${questionSetId}/getQuestionSetInfo`),
        axiosInstance.get(`/knovia/myInfo/${questionSetId}/getQuestionSetColumns`),
        axiosInstance.get(`/knovia/myInfo/${questionSetId}/rewardStatus`),
      ]);

      console.log('QuestionSet Loaded:', questionSetResponse.data);
      console.log('Columns in QuestionSet Loaded:', columnResponse.data);
      console.log('Reward Status:', rewardStatusResponse.data);

      setMyInfoQuestionSet(questionSetResponse.data);
      setQuestionSetRewardStatus(rewardStatusResponse.data);

      // Sort by `order` before setting state
      const columnData = [...columnResponse.data].sort((a, b) => a.order - b.order);
      setMyInfoQuestionSetColumns(columnData);

      const renderColumns = transformToRenderedColumns(columnData, questionSetResponse.data);
      console.log('renderColumns:', renderColumns);
      setMyInfoQuestionSetRenderColumns(renderColumns);
      
      return {
        questionSet: questionSetResponse.data,
        columnData: columnData,
      };
    } catch (err) {
      console.error(err);
      setDataFetchError("Failed to load data.");
      return null; // <- This ensures the caller knows the fetch failed
    } finally {
      setQuestionSetLoading(false);
    }
  };

  const fetchAnswers = async (columnData) => {
    try {

      console.log('columnData:', columnData);

      const questionIds = columnData.filter(c => c.type==="question").map(q => q._id);
      const answersRes = await axiosInstance.post(
        `/knovia/myInfo/questionSet/getAnswers`,
        { questionSetId, questionIds }
      );
      console.log('answersRes.data:', answersRes.data);
      const userAnswers = answersRes.data.answers;

      const initialAnswers = {};
      const initialHasVerifiedAnswers = {};
      const initialVerifiedAnswers = {};
      const initialHasConfirmedAnswers = {};
      const initialConfirmedAnswers = {};
      const initialResolvedAnswerBuckets = {};

      questionIds.forEach((questionId) => {
        initialAnswers[questionId] = userAnswers[questionId]?.answer || "";
        if(userAnswers[questionId]?.answer.trim().length){
          initialHasVerifiedAnswers[questionId] = userAnswers[questionId]?.isAIVerified || false;
          initialVerifiedAnswers[questionId] = userAnswers[questionId]?.answer || "";
          initialHasConfirmedAnswers[questionId] = userAnswers[questionId]?.answer || false;
          initialConfirmedAnswers[questionId] = userAnswers[questionId]?.answer || "";
          initialResolvedAnswerBuckets[questionId] = userAnswers[questionId]?.resolvedStatsOption || "";
        }
      });

      setAnswers(initialAnswers);
      setHasVerifiedAnswers(initialHasVerifiedAnswers);
      setVerifiedAnswers(initialVerifiedAnswers);
      setHasConfirmedAnswers(initialHasConfirmedAnswers);
      setConfirmedAnswers(initialConfirmedAnswers);
      setResolvedAnswerBuckets(initialResolvedAnswerBuckets);

    } catch (err) {
      console.error(err);
      setDataFetchError("Failed to load answers");
      return null; // <- This ensures the caller knows the fetch failed
    } finally {
      setAnswerLoading(false);
    }
  }


  //--------------------------------------------------
  // Fetch Images
  //--------------------------------------------------
  const [questionSetThumbnailUrl, setQuestionSetThumbnailUrl] = useState("");

  const fetchQuestionSetThumbnail = (qs) => {
    console.log("Start fetching questionSet thumbnail");
    console.log('qs:', qs);
    fetchThumbnailsWithCache(
      [qs],
      "infoQuestionSet",
      "sizeMedium",
      ({ id, url }) => {
        setQuestionSetThumbnailUrl(prev => url);
      },
      () => {
        console.log("QuestionSet thumbnail finished loading");
      }
    );
  };

  const [imageMap, setImageMap] = useState(new Map());

  const createdObjectUrls = new Set(); // track urls
  const fetchColumnImages = async (columnData) => {
    console.log("start fetching column images");
    const imageEntries = columnData.filter(s => s.type === "image");
    const newMap = new Map(imageMap); // preserve existing

    console.log('imageEntries:', imageEntries);

    await Promise.all(
      imageEntries.map(async (entry) => {
        const filename = entry.image;
        if (!filename || newMap.has(filename)) return;

        try {
          const { blob } = await fetchImage(filename, "original");
          const objectUrl = URL.createObjectURL(blob);
          createdObjectUrls.add(objectUrl); // track it
          newMap.set(filename, objectUrl);
        } catch (e) {
          console.error("Failed to load image:", filename);
        }
      })
    );

    setImageMap(newMap);
  };

  const [progress, setProgress] = useState({});

  const fetchProgress = async () => {
    const progressRes = await axiosInstance.get(`/knovia/myInfo/${questionSetId}/progress`);
    console.log('progressRes.data:', progressRes.data);
    setProgress(progressRes.data);
  }

  useEffect(() => {
    return () => {
      for (const url of createdObjectUrls) {
        URL.revokeObjectURL(url);
      }
      createdObjectUrls.clear(); // clean the tracker
    };
  }, []);

  const loadQuestionSetData = async () => {
    const dataFetched = await fetchData();
    if (!dataFetched) return;
    const { questionSet, columnData } = dataFetched;

    if (questionSet) {
      fetchAnswers(columnData);
      fetchProgress();
      fetchQuestionSetThumbnail(questionSet);
      fetchColumnImages(columnData);
    }
  };

  useEffect(() => {
    loadQuestionSetData();
  }, [questionSetId]);



  //------------------------------------------------
  // Load Stats
  //------------------------------------------------
  const [questionStats, setQuestionStats] = useState({});
  const fetchedStatsRef = useRef(new Set());  // track fetched question IDs

  const fetchStats = async (statsIds, force = false) => {
    // Filter out already fetched IDs unless force is true
    const idsToFetch = force
      ? statsIds
      : statsIds.filter(id => !fetchedStatsRef.current.has(id));

    if (idsToFetch.length === 0) return;

    try {
      const res = await axiosInstance.get(`/knovia/myInfo/questions/getStats`, {
        params: { statsIds: idsToFetch, questionSetId }
      });
      setQuestionStats(prev => ({
        ...prev,
        ...res.data.stats
      }));
      idsToFetch.forEach(id => fetchedStatsRef.current.add(id));
      console.log("Stats fetched:", res.data.stats);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const loadStats = async (ids = null) => {
    const finalIds = ids ?? Object.keys(confirmedAnswers).filter(c=>confirmedAnswers[c]);
    if (finalIds.length === 0) return;
    await fetchStats(finalIds);
  };

  const forceReloadStats = async (ids = null) => {
    const finalIds = ids ?? Object.keys(confirmedAnswers).filter(c=>confirmedAnswers[c]);
    if (finalIds.length === 0) return;
    await fetchStats(finalIds, true);
  };

  useEffect(() => {
    console.log('confirmedAnswers:', confirmedAnswers);
    //loadStats();
    forceReloadStats();
  }, [confirmedAnswers]);


  //------------------------------------------------
  // Verify and Save
  //------------------------------------------------

  // Handle Verification
  const handleVerify = async (questionId) => {

    const answer = answers[questionId];
    if (!answer.trim()) return; // Prevent verifying empty answers

    // Prevent redundant verification if the answer hasn't changed
    if (verifiedAnswers[questionId] === answer.trim()) {
      console.log(`Answer for question ${questionId} has not changed, skipping verification.`);
      return;
    }

    setAnswerAwaitVerifying(prev => questionId);

    try {
      const res = await axiosInstance.post("/knovia/myInfo/verifyAnswer", {
        questionId,
        answer,
      });

      const { isValid, results } = res.data;
      console.log('isValid:', isValid);
      console.log('results:', results);

      if(showVerificationRules){
        // Expand verification statuses for all conditions
        const newVerificationStatus = [];
        const verifyRuleAI = infoQuestions.find(q => q._id === questionId)?.verifyRuleAI;
        if (verifyRuleAI) {
          verifyRuleAI.forEach((ruleSet, setIdx) => {
            ruleSet.conditions.forEach(() => {
              if (results[setIdx]?.decision === "YES") {
                newVerificationStatus.push("YES");
              } else {
                newVerificationStatus.push("NO");
              }
            });
          });
        }
        setRuleVerificationStatus((prev) => ({
          ...prev,
          [questionId]: newVerificationStatus,
        }));
      }

      if (isValid) {
        setHasVerifiedAnswers((prev) => ({ ...prev, [questionId]: true }));
        setVerifiedAnswers((prev) => ({ ...prev, [questionId]: answer.trim() }));
        setHasConfirmedAnswers((prev) => ({ ...prev, [questionId]: false }));
        console.log(`Answer verified for question ${questionId}.`);
      } else {
        setVerifiedAnswers((prev) => ({ ...prev, [questionId]: null }));
        setHasWrongAnswers((prev) => ({ ...prev, [questionId]: true }));
        console.log(`Verification failed:`, results);
      }

      setConfirmedAnswers((prev) => ({
        ...prev,
        [questionId]: undefined, // Reset confirmed answers
      }));

      setAnswerAwaitVerifying(prev => "");
    } catch (error) {
      setAnswerAwaitVerifying(prev => "");
      console.error("Error verifying answer", error);
    }
  };

  // Handle Saving
  const handleSave = async (questionId) => {
    if (!hasVerifiedAnswers[questionId]) return; // Prevent saving if not verified
    if (answerAwaitSaving) return; // Debounce
    setAnswerAwaitSaving(prev => questionId);

    const answer = answers[questionId];

    try {
      const updateResult = await axiosInstance.post("/knovia/myInfo/updateAnswer", {
        questionId,
        answer,
      });

      const { message, hasAttributesAdded } = updateResult.data;
      console.log('hasAttributesAdded:', hasAttributesAdded);

      setHasConfirmedAnswers((prev) => ({
          ...prev,
          [questionId]: true,
        }));
      setConfirmedAnswers((prev) => ({
        ...prev,
        [questionId]: answer,
      }));

      // Fetch Question Array again
      /*
      if(hasAttributesAdded){
        console.log('fetching infoData due to new attributes added...');
        await fetchData();
      }
      */
      loadQuestionSetData();

      setTimeout(() => {
        setAnswerAwaitSaving("");
      }, 500);
      console.log(`Saved answer for question ${questionId}:`, answer);
    } catch (error) {
      setAnswerAwaitSaving(prev => "");
      console.error("Error saving answer", error);
    }
  };


  const resetQuestionSetAnswer = async () => {
    try {
      const resetResult = await axiosInstance.post(
        `/knovia/myInfo/${questionSetId}/resetQuestionSetAnswers`, {
      });

      console.log('QuestionSet reset result:', resetResult.data);

      fetchAnswers(myInfoQuestionSetColumns);
    } catch (error) {
      console.error("Error reseting questionSet", error);
    }
  };

  //--------------------------------------------------
  // Horizontal Section Menu
  //--------------------------------------------------

  const sectionMenuWrapperRef = useRef(null);
  const handleScrollLeft = (index) => {
    const wrapper = sectionMenuWrapperRef.current;
    const item = wrapper?.children[index];
    if (!wrapper || !item) return;

    const wrapperRect = wrapper.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();

    const offsetLeft = item.offsetLeft;
    const desiredLeft = 60;

    const newScrollLeft = offsetLeft - desiredLeft;

    // Clamp value to prevent overscroll
    const maxScrollLeft = wrapper.scrollWidth - wrapper.clientWidth;
    wrapper.scrollTo({
      left: Math.min(Math.max(newScrollLeft, 0), maxScrollLeft),
      behavior: "smooth"
    });
  }


  //--------------------------------------------------
  // Scroll to Section
  //--------------------------------------------------

  const sectionRefs = useRef([]);
  sectionRefs.current = myInfoQuestionSetRenderColumns.map(
    (_, i) => sectionRefs.current[i] ?? React.createRef()
  );
  const handleScrollToSection = (index, offset = 110) => {
    const sectionEl = sectionRefs.current[index]?.current;
    if (sectionEl) {
      const rect = sectionEl.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const targetY = rect.top + scrollTop - offset;

      window.scrollTo({
        top: targetY,
        behavior: "smooth",
      });
    }
  };

  const [showCheck, setShowCheck] = useState(false);
  const [showCheckWidth, setShowCheckWidth] = useState(false);
  useEffect(()=>{
    if(questionSetRewardStatus?.isCompleted){
      setShowCheck(true);
    }
  },[questionSetRewardStatus]);

  // ---- put near other top-level hooks ----
  const [previewOpen, setPreviewOpen] = useState(false);   // modal on/off
  const [previewSrc,  setPreviewSrc]  = useState(null);    // full-size URL


  const pRef = useAutoFontResize(myInfoQuestionSet.label, 20, 12);



  const [resizeSignal, setResizeSignal] = useState(0);

  useEffect(() => {
    let timeout;
    const handleResize = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        setResizeSignal(prev => prev + 1);
      }, 100); // Debounce 100ms
    };

    window.addEventListener("resize", handleResize);
    return () => {
      clearTimeout(timeout);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  //--------------------------------------------------
  // Return
  //--------------------------------------------------

  return (

    <div className="page-content question-set-input" 
      style={{ "--page-content-background": `url(${home4})` }}
    >

      <Container fluid className="question-set-list-container">

        <Card className="question-set-input-card"
          style={{ minHeight: "calc(100vh - 60px)" }}
        >
          <CardBody className="question-set-input-card-body">

            {/*------ Small Screen Top Bar ------*/}
            <div className="d-block d-md-none question-set-input-title-outer-div">


              {/*---- Title ----*/}
              <div className="question-set-input-title-row">
                <div style={{ cursor:'pointer' }}
                  onClick={handlePageBack}
                >
                  <ArrowLeft />
                </div>

                {/* Label and Description */}
                <div style={{ flex: "1", overflowX: "auto", scrollbarWidth: "none", msOverflowStyle: "none", marginRight: "12px" }}
                  className="scroll-container-no-scrollbar"
                >
                  <div style={{ whiteSpace: "nowrap", userSelect: "none" }}>
                    <div className="question-set-label-text">
                      <p ref={pRef} className="mb-0" style={{ display: "inline", marginRight: "3px" }}>
                        {
                          myInfoQuestionSet?.label?.length > 18 
                          ? myInfoQuestionSet?.label.slice(0,myInfoQuestionSet?.label?.length)
                          : myInfoQuestionSet?.label
                        }
                      </p>
                    </div>
                    {myInfoQuestionSet.description && (
                      <div className="question-set-label-description">
                        <p className="mb-0" style={{ display: "inline" }}>
                          {
                            myInfoQuestionSet?.description?.length > 22 
                            ? myInfoQuestionSet?.description.slice(0,myInfoQuestionSet?.description?.length)
                            : myInfoQuestionSet?.description
                          }
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Tool List */}
                <div className="me-3 d-flex justify-content-end align-items-center" style={{ width: '100px' }}>

                  <div className="d-none d-sm-block me-2" style={{ width: showCheckWidth ? '30px' : '0px' , height: '30px' }}>
                    <CheckReveal visible={showCheck} setVisible={setShowCheck} hasWidth={showCheckWidth} setHasWidth={setShowCheckWidth} size={30} />
                  </div>

                  <div className="question-set-top-icon-ticket" 
                    style={{ marginRight: "12.5px", cursor: "pointer" }}
                    onClick={() => { navigate(`/myInfo/questionSets/${myInfoQuestionSet?._id}/redeemReward`) }}
                  >
                    <TicketCheck 
                      color={questionSetRewardStatus?.isCompleted ? '#68cd72' : '#6c757d'}
                    />
                  </div>

                  {/*
                  <div className="question-set-top-icon-refresh" style={{ marginRight: "12.5px", cursor: "pointer" }}
                    onClick={resetQuestionSetAnswer}
                  >
                    <RefreshCcw />
                  </div>
                  */}
                  <div className="question-set-top-icon-message" style={{ marginRight: "12.5px", cursor: "pointer" }}>
                    <MessageSquareText />
                  </div>

                  {/* ⬇️ New Three Dots Dropdown */}
                  <UncontrolledDropdown direction="down">
                    <DropdownToggle tag="span" style={{ cursor: 'pointer' }}>
                      <MoreVertical />
                    </DropdownToggle>
                    {/* Custom styling to push the menu downward */}
                    <DropdownMenu end style={{ marginTop: '8px' }}>
                      <DropdownItem onClick={() => console.log('Edit clicked')}>
                        <Edit2 size={14} className="me-2 text-info" />
                        Edit
                      </DropdownItem>
                      <DropdownItem onClick={() => console.log('Delete clicked')}>
                        <Trash2 size={14} className="me-2 text-danger" />
                        Delete
                      </DropdownItem>
                      <DropdownItem onClick={resetQuestionSetAnswer}>
                        <RefreshCcw size={14} className="me-2 text-warning" style={{ marginBottom: "3px" }}/>
                        重填題組
                      </DropdownItem>
                    </DropdownMenu>
                  </UncontrolledDropdown>

                </div>
              </div>

              {progress && (
                <div style={{ padding: '0px 10px' }}>
                  <QuestionSetProgress
                    answeredCount={progress.answeredCount}
                    totalQuestions={progress.totalQuestions}
                  />
                </div>
              )}


              {/*---- Section Menu ----*/}
              <div className="question-set-input-section-menu">
                <div className="question-set-input-section-menu-expand">
                  <CircleChevronRight size={18}/>
                </div>
                <div className="question-set-input-section-menu-item-wrapper"
                  ref={sectionMenuWrapperRef}
                >
                  {[...myInfoQuestionSetRenderColumns].map((section, index) => {
                    const displayLabel = section.label || myInfoQuestionSet.label;
                    return (
                      <div key={index} className={`question-set-input-section-menu-item ${activeSectionIndex===index && 'active'}`}
                        onClick={()=>{
                          setActiveSectionIndex(prev => index);
                          handleScrollLeft(index);
                          handleScrollToSection(index, 110);
                        }}
                      >
                        <p className="mb-0">{displayLabel.length > 8 ? displayLabel.slice(0,6)+'...' : displayLabel}</p>
                      </div>
                    )
                  })}
                </div>
              </div>

            </div>


            {myInfoQuestionSet?.topic?.label && (
              <div 
                className="d-flex justify-content-end align-items-center" 
                style={{
                  margin: "24px 0px 0px 0px",
                  paddingRight: "10px"
                }}
              >
                <div style={{ paddingRight: "10px", fontSize: "11px", cursor: "pointer", maxWidth: "150px" }}
                  onClick={()=>{ navigate(`/myInfo/storeMenu/topics/${myInfoQuestionSet?.topic._id}`); }}
                >
                  <p className="mb-0" style={{ color: "#4988d8" }}>
                    主題：{myInfoQuestionSet?.topic?.label.length > 15 ? myInfoQuestionSet?.topic?.label.slice(0,15)+'...' :  myInfoQuestionSet?.topic?.label}
                  </p>
                </div>
              </div>
            )}

            {/*------ Small Screen ------*/}
            <div className="d-block d-md-none question-set-input-section-wrapper">
              {/*
              <Button onClick={() => {
                setShowCheck(prev => true);
              }}>
                Show
              </Button>
              <Button onClick={() => {
                setShowCheck(prev => false);
              }}>
                Hide
              </Button>
              */}

              {myInfoQuestionSetRenderColumns.map((section, index) => {
                return (
                  <div key={index} 
                    ref={sectionRefs.current[index]}
                    className="question-set-input-section-outer-div"
                    style={{ 
                      paddingBottom: index <= 10 ? "10px" : undefined,
                    }}
                  >
                    {section.label && 
                      <div className="question-set-input-section-title-div">
                        <p className="mb-0 question-set-input-section-title">{section.label}</p>
                      </div>
                    }

                    {section.sectionContent.map((item, subIndex) => {
                      if (item.type === "text") {
                        return (
                          <div key={subIndex}>
                            <pre className="question-set-input-text">{item.text}</pre>
                          </div>
                        )
                      }
                      if (item.type === "image") {
                        return (
                          <div key={subIndex} className="question-set-input-image-outer-div">
                            <img
                              src={imageMap.get(item.imageUrl) || placeholder_image}
                              onError={(e) => {
                                e.target.onerror = null; // Prevent infinite loop
                                e.target.src = placeholder_image;
                              }}
                              className="question-set-input-image"
                              alt="question-set-input-image"
                              onClick={() => {
                                // 7. choose original if we cached it, else the same one
                                setPreviewSrc(imageMap.get(item.imageUrl) || item.imageUrl);
                                // 8. open the modal
                                setPreviewOpen(true);
                              }}
                              style={{ cursor: "zoom-in" }}  // 9. visual cue
                            />
                          </div>
                        )
                      }
                      if (item.type === "question") {
                        return (
                          <QuestionPane
                            key={subIndex}
                            subIndex={subIndex}
                            myInfoQuestionSet={myInfoQuestionSet}
                            infoQuestion={item}

                            resizeSignal={resizeSignal}

                            visibleStats={visibleStats}
                            toggleStatsOpen={toggleStatsOpen}
                            animatingStats={animatingStats}
                            
                            questionStats={questionStats}
                            setQuestionStats={setQuestionStats}
                            loadStats={forceReloadStats}
                            resolvedAnswerBuckets={resolvedAnswerBuckets}

                            answers={answers}
                            hasVerifiedAnswers={hasVerifiedAnswers}
                            hasWrongAnswers={hasWrongAnswers}
                            verifiedAnswers={verifiedAnswers}
                            answerAwaitVerifying={answerAwaitVerifying}
                            hasConfirmedAnswers={hasConfirmedAnswers}
                            confirmedAnswers={confirmedAnswers}
                            answerAwaitSaving={answerAwaitSaving}

                            showAffectedAttributes={showAffectedAttributes}
                            showVerificationRules={showVerificationRules}
                            showActivationConditions={showActivationConditions}
                            ruleVerificationStatus={ruleVerificationStatus}
                            
                            handleInputChange={handleInputChange}
                            handleVerify={handleVerify}
                            handleSave={handleSave}
                          />
                        )
                      }
                      return null;
                    })}
                  </div>
                );
              })}
            </div>

          </CardBody>
        </Card>

      </Container>

      <PreviewImageModal
        open={previewOpen}
        src={previewSrc}
        toggle={() => setPreviewOpen(false)}
      />
    </div>
  );
};

export default withTranslation()(QuestionSetInput);


